import { differenceInDays } from "date-fns";

/*
 * The order pipeline is derived from the signing-assistant payload the dashboard
 * already holds, rather than from a second aggregate of its own. That is what
 * makes these figures agree with Sales Overview and the sidebar's unsigned badge
 * instead of merely being kept in step with them.
 *
 * Two consequences of the source are worth knowing:
 *
 *   - getWithNewSales drops orders awaiting image moderation before it responds,
 *     so nothing here counts them. imageApprovalsPending is passed in from the
 *     image approval queue, which owns that definition.
 *   - The payload carries no shipment or tracking columns, so there is no
 *     received-but-not-shipped figure.
 */

const AGE_BUCKETS = [7, 14, 30];

const toDate = (value) => (value ? new Date(value) : null);

const ageInDays = (value, now) => {
  const from = toDate(value);

  return from ? differenceInDays(now, from) : null;
};

const salesOf = (signingData) =>
  Object.values(signingData ?? {}).flatMap((group) => group?.sales ?? []);

// Cancelled and damaged orders are superseded rather than outstanding, and Sales
// Overview hides both — counting them would give an admin a number they cannot
// drill into on the page this panel links to.
const isLiveOrder = (sale) => !sale.cancelled && !sale.damaged;

const isAwaitingFulfillment = (sale) => !sale.signed;

const isAwaitingArrival = (sale) => Boolean(sale.signed) && !sale.received;

const isOverdue = (sale, now) =>
  Boolean(sale.completion_deadline) && toDate(sale.completion_deadline) < now;

// Fulfilled items age from the day the shop finished with them; an order with no
// completion timestamp falls back to when it was placed.
const settledAt = (sale) => sale.ended_fulfillment ?? sale.created;

const atLeast = (days) => (age) => age !== null && age >= days;

const oldestOf = (ages) =>
  ages.length === 0
    ? null
    : ages.reduce((oldest, age) => Math.max(oldest, age));

const bucketsOf = (ages) =>
  AGE_BUCKETS.reduce(
    (buckets, days) => ({
      ...buckets,
      [`over${days}`]: ages.filter(atLeast(days)).length,
    }),
    { total: ages.length },
  );

export const orderPipeline = ({ signingData, imageApprovalsPending }, now) => {
  const live = salesOf(signingData).filter(isLiveOrder);
  const awaiting = live.filter(isAwaitingFulfillment);
  const arriving = live.filter(isAwaitingArrival);

  return {
    awaitingFulfillment: awaiting.length,
    overdue: awaiting.filter((sale) => isOverdue(sale, now)).length,
    oldestPendingDays: oldestOf(
      awaiting
        .map((sale) => ageInDays(sale.created, now))
        .filter(Number.isFinite),
    ),
    signedNotReceived: bucketsOf(
      arriving.map((sale) => ageInDays(settledAt(sale), now)),
    ),
    imageApprovalsPending: Number(imageApprovalsPending ?? 0),
  };
};

export default { orderPipeline };
